import { createEffect, createMemo, createSignal, For, Show, untrack } from "solid-js";
import { Link } from "@/lib/u-router";
import { ChefHat, Download, Plus, Search, Users } from "lucide-solid";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TextField } from "@/components/ui/text-field";
import {
  buildRecipeDataset,
  exportDatasetAsCsv,
  exportDatasetAsPdf,
  parseStringArray,
  restoreRecipeMutation,
  trashRecipeMutation,
} from "@mealplan/shared";
import { useRecipesQuery } from "@mealplan/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { msg } from "@lingui/core/macro";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import { registerUndo } from "@/lib/undo";
import { undoMessages } from "@/lib/undo-messages";
import { showToast } from "@/components/ui/toast";
import { SwipeToDeleteRow } from "@/components/ui/swipe-to-delete-row";
import { EmptyRecipesIllustration } from "@/components/ui/illustrations";

interface RecipeListViewProps {
  /** Filtros iniciales desde la URL (deep-link `?q=` / `?tag=`). */
  initialSearch?: string;
  initialTag?: string;
  /** Reflejan el filtro a la URL (idempotente, `replace`). */
  onSearchChange?: (q: string) => void;
  onTagChange?: (tag: string) => void;
}

/**
 * Export menu: an "Export" button that opens a popover with CSV/PDF choices.
 * The data plumbing lives in the parent (`onExport`); this is purely the
 * trigger UI (K1 canon ExportMenu).
 */
function ExportMenu(props: { onExport: (format: "csv" | "pdf") => void }) {
  const { t } = useI18n();
  return (
    <Popover>
      <PopoverTrigger
        as={Button}
        variant="outline"
        size="sm"
        class="h-8 gap-1.5 text-xs"
        data-testid="recipe-export"
      >
        <Download class="h-3.5 w-3.5" />
        {t(msg`Export`)}
      </PopoverTrigger>
      <PopoverContent class="w-44 p-2">
        <div class="flex flex-col gap-1">
          <Button
            variant="ghost"
            size="sm"
            class="justify-start h-8 text-xs"
            onClick={() => props.onExport("csv")}
          >
            {t(msg`Export CSV`)}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            class="justify-start h-8 text-xs"
            onClick={() => props.onExport("pdf")}
          >
            {t(msg`Export PDF`)}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function RecipeListView(props: RecipeListViewProps = {}) {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  // Los filtros son deep-linkables: la URL (`?q=` + `?tag=`) es la fuente de
  // verdad al cargar/compartir. Recargar la URL reconstruye exactamente la vista.
  const [search, setSearch] = createSignal(props.initialSearch ?? "");
  const [activeTag, setActiveTag] = createSignal(props.initialTag ?? "");

  // Si la URL cambia por fuera (back/forward, link pegado), refleja en signals.
  createEffect(() => {
    const q = props.initialSearch ?? "";
    const tag = props.initialTag ?? "";
    if (q !== untrack(search)) {
      setSearch(q);
    }
    if (tag !== untrack(activeTag)) {
      setActiveTag(tag);
    }
  });

  const recipes = useRecipesQuery({
    db,
    drizzle,
    userId: session.userId,
    search,
    tag: activeTag,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  // ── Export ────────────────────────────────────────────────────────────────
  // Exports the recipes currently shown in this view (after search/tag filters)
  // to CSV or PDF via the shared, entity-agnostic export utility. The recipe
  // adapter lives in @mealplan/shared. Reads local-first from the live query.
  const handleExport = (format: "csv" | "pdf") => {
    const currentRecipes = recipes.data ?? [];
    const dataset = buildRecipeDataset(currentRecipes, {
      title: t(msg`Recipes`),
      subtitle: t(msg`Exported recipes`),
    });
    if (format === "csv") {
      exportDatasetAsCsv(dataset);
    } else {
      exportDatasetAsPdf(dataset);
    }
  };

  const allTags = createMemo(() => {
    const tags = new Set<string>();
    for (const recipe of recipes.data ?? []) {
      for (const tag of parseStringArray(recipe.tags)) {
        tags.add(tag);
      }
    }
    return [...tags].toSorted((a, b) => a.localeCompare(b));
  });

  return (
    <div class="space-y-6">
      <div class="flex items-center justify-between gap-3">
        <h2 class="font-serif text-2xl font-semibold tracking-tight">{t(msg`Recipes`)}</h2>
        <div class="flex items-center gap-2">
          <ExportMenu onExport={handleExport} />
          <Link to="/recipes/new">
            <Button size="sm" class="gap-2" data-testid="new-recipe">
              <Plus class="h-4 w-4" />
              {t(msg`New recipe`)}
            </Button>
          </Link>
        </div>
      </div>

      <div class="relative">
        <Search class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <TextField
          class="pl-9"
          placeholder={t(msg`Search recipes...`)}
          value={search()}
          onInput={(e) => {
            const val = e.currentTarget.value;
            setSearch(val);
            props.onSearchChange?.(val);
          }}
          data-testid="recipe-search"
        />
      </div>

      <Show when={allTags().length > 0}>
        <div class="flex flex-wrap gap-2">
          <For each={allTags()}>
            {(tag) => (
              <button
                type="button"
                onClick={() => {
                  const next = activeTag() === tag ? "" : tag;
                  setActiveTag(next);
                  props.onTagChange?.(next);
                }}
              >
                <Badge variant={activeTag() === tag ? "default" : "outline"}>{tag}</Badge>
              </button>
            )}
          </For>
        </div>
      </Show>

      <Show
        when={(recipes.data ?? []).length > 0}
        fallback={
          <Show when={!recipes.isLoading}>
            <div class="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-surface/40 p-12 text-center">
              <EmptyRecipesIllustration class="mb-4 h-32 w-40 text-muted-foreground" />
              <h3 class="font-serif text-xl font-semibold">{t(msg`No recipes yet`)}</h3>
              <p class="mt-1 max-w-xs text-sm text-muted-foreground">
                {t(msg`Create your first recipe to start planning your week.`)}
              </p>
              <Link to="/recipes/new" class="mt-5">
                <Button class="gap-2">
                  <Plus class="h-4 w-4" />
                  {t(msg`New recipe`)}
                </Button>
              </Link>
            </div>
          </Show>
        }
      >
        <div class="grid gap-3 sm:grid-cols-2">
          <For each={recipes.data}>
            {(recipe, index) => {
              const handleDelete = (): void => {
                const dbInstance = drizzle();
                if (!dbInstance) {
                  return;
                }
                // Borrar desde la UI = mover a la papelera (soft-delete). El undo
                // L1 (pill/Ctrl+Z) restaura; la papelera es el respaldo duradero.
                void (async () => {
                  try {
                    await trashRecipeMutation(dbInstance, recipe.id);
                    registerUndo(undoMessages(t, t(msg`Recipe moved to trash`)), async () => {
                      await restoreRecipeMutation(dbInstance, recipe.id);
                    });
                  } catch (err: unknown) {
                    console.error("Error moving recipe to trash:", err);
                    showToast(t(msg`Something went wrong`), "error");
                  }
                })();
              };
              return (
                <SwipeToDeleteRow
                  onDelete={handleDelete}
                  allowControlDrag
                  class="rounded-xl animate-list-item-in"
                  style={{ "animation-delay": `${Math.min(index(), 8) * 35}ms` }}
                >
                  <Link
                    to="/recipes/$recipeId"
                    params={{ recipeId: recipe.id }}
                    class="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
                  >
                    <Card
                      class="group h-full overflow-hidden transition-[transform,box-shadow,border-color] duration-200 will-change-transform hover:-translate-y-0.5 hover:border-accent/30 hover:shadow-md active:translate-y-0 active:scale-[0.99]"
                      data-testid="recipe-card"
                    >
                      <CardContent class="flex items-center gap-3 p-4">
                        <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent transition-colors group-hover:bg-accent/15">
                          <ChefHat class="h-6 w-6" />
                        </div>
                        <div class="min-w-0 flex-1">
                          <p class="truncate font-serif text-base font-semibold leading-tight">
                            {recipe.name}
                          </p>
                          <p class="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                            <Users class="h-3 w-3" />
                            {t(msg`{count} servings`, { count: recipe.servings ?? 0 })}
                          </p>
                          <Show when={parseStringArray(recipe.tags).length > 0}>
                            <div class="mt-2 flex flex-wrap gap-1">
                              <For each={parseStringArray(recipe.tags).slice(0, 3)}>
                                {(tag) => <Badge variant="outline">{tag}</Badge>}
                              </For>
                            </div>
                          </Show>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </SwipeToDeleteRow>
              );
            }}
          </For>
        </div>
      </Show>
    </div>
  );
}
